import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import {
  Image as ImageIcon,
  VideoLibrary,
  Audiotrack,
  Description,
  DeleteOutline,
  DragIndicator,
  Link as LinkIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  mediaItem: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.mode === 'dark' ? '#374151' : "#f5f5f5",
    borderRadius: "8px",
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : "#eeeeee",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  
  dragHandle: {
    cursor: "grab",
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    "&:active": {
      cursor: "grabbing",
    },
  },
  
  mediaIcon: {
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
  },
  
  mediaInfo: {
    flex: 1,
    minWidth: 0,
  },
  
  mediaName: {
    fontWeight: "bold",
    fontSize: "14px",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  
  mediaDetails: {
    fontSize: "12px",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  
  mediaTypeChip: {
    marginRight: theme.spacing(1),
    height: "24px",
    fontSize: "11px",
  },
  
  externalChip: {
    backgroundColor: "#ff9800",
    color: "white",
    height: "20px",
    fontSize: "10px",
  },
  
  deleteButton: {
    color: "#d32f2f",
    padding: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: "rgba(211, 47, 47, 0.1)",
    },
  },
  
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
}));

const getMediaIcon = (mediaType) => {
  switch (mediaType) {
    case "image":
      return <ImageIcon />;
    case "video":
      return <VideoLibrary />;
    case "audio":
      return <Audiotrack />;
    case "document":
      return <Description />;
    default:
      return <Description />;
  }
};

// ✅ PASO 1: Función SIMPLE para formatear tamaño - SIN limpiezas adicionales
const formatFileSize = (bytes) => {
  if (!bytes || bytes === null || bytes === undefined) return "N/A";
  const size = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  if (isNaN(size)) return "N/A";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const PromptMediaPreview = ({ mediaFiles, onDelete, onReorder, isDragging }) => {
  const classes = useStyles();

  if (!mediaFiles || mediaFiles.length === 0) {
    return (
      <Paper className={classes.emptyState}>
        <Typography variant="body2" color="textSecondary">
          No hay archivos multimedia agregados
        </Typography>
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
          Agrega hasta 6 archivos (imágenes, videos, audios o documentos)
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {mediaFiles.map((media, index) => {
        const displayName = media.mediaName || "Sin nombre";
        const fileSize = media.mediaSize ? formatFileSize(media.mediaSize) : null;
        
        return (
        <Paper
          key={media.id || index}
          className={classes.mediaItem}
          style={{
            opacity: isDragging === index ? 0.5 : 1,
          }}
        >
          {onReorder && (
            <Tooltip title="Arrastrar para reordenar">
              <IconButton
                className={classes.dragHandle}
                size="small"
                onMouseDown={(e) => {
                  if (onReorder) {
                    onReorder(index, "start");
                  }
                }}
              >
                <DragIndicator />
              </IconButton>
            </Tooltip>
          )}
          
          <Box className={classes.mediaIcon}>
            {getMediaIcon(media.mediaType)}
          </Box>
          
          <Box className={classes.mediaInfo}>
            <Box display="flex" alignItems="center" marginBottom={0.5}>
              <Typography 
                className={classes.mediaName}
                component="span"
              >
                {displayName}
              </Typography>
              {media.isExternal && (
                <Chip
                  icon={<LinkIcon style={{ fontSize: 14 }} />}
                  label="URL Externa"
                  size="small"
                  className={classes.externalChip}
                  style={{ marginLeft: 8 }}
                />
              )}
            </Box>
            
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              <Chip
                label={media.mediaType?.toUpperCase() || "N/A"}
                size="small"
                className={classes.mediaTypeChip}
                color="primary"
                variant="outlined"
              />
              {!media.isExternal && fileSize && (
                <Typography 
                  className={classes.mediaDetails}
                  component="span"
                >
                  {fileSize}
                </Typography>
              )}
              {media.isExternal && (
                <Typography className={classes.mediaDetails} style={{ marginLeft: 8 }}>
                  {media.mediaUrl?.substring(0, 50)}...
                </Typography>
              )}
            </Box>
            
            {media.caption && (
              <Typography className={classes.mediaDetails} style={{ marginTop: 4 }}>
                {media.caption}
              </Typography>
            )}
          </Box>
          
          {onDelete && (
            <Tooltip title="Eliminar archivo">
              <IconButton
                className={classes.deleteButton}
                size="small"
                onClick={() => onDelete(media.id)}
              >
                <DeleteOutline />
              </IconButton>
            </Tooltip>
          )}
        </Paper>
        );
      })}
    </Box>
  );
};

export default PromptMediaPreview;
